import * as yup from 'yup';
import { ControlModeTemplateFieldsType } from '@src/types/ControlModes';

// Schema for range (min/max)
export const rangeSchema = yup.object().shape({
  min: yup.number().nullable(),
  max: yup
    .number()
    .nullable()
    .test(
      'min-max',
      'Max value must be greater than min value',
      function (max) {
        const { min } = this.parent;
        if (
          min !== undefined &&
          min !== null &&
          max !== undefined &&
          max !== null
        ) {
          return max > min;
        }
        return true;
      },
    ),
});

// Schema for fields
const fieldSchema = yup.object().shape({
  uiLabel: yup.string().required('UI Label is required'),
  controllerField: yup.string().required('Controller Field is required'),
  type: yup
    .string()
    .oneOf(
      Object.values(ControlModeTemplateFieldsType),
      'Type must be one of: string, number, boolean, select',
    )
    .required('Type is required'),
  range: yup.mixed().when('type', {
    is: ControlModeTemplateFieldsType.NUMBER,
    then: () => rangeSchema,
    otherwise: () => yup.mixed().nullable(),
  }),
  listOptions: yup.mixed().when('type', {
    is: ControlModeTemplateFieldsType.SELECT,
    then: () =>
      yup
        .array()
        .of(yup.string())
        .min(1, 'At least one option is required for select fields')
        .required('List options are required for select fields'),
    otherwise: () => yup.mixed().nullable(),
  }),
  defaultValue: yup
    .mixed()
    .test(
      'valid-default-value',
      'Default value must match the field type',
      function (value) {
        if (value === undefined || value === null) {
          return true;
        }

        const { type } = this.parent;

        if (
          type === ControlModeTemplateFieldsType.STRING ||
          type === ControlModeTemplateFieldsType.SELECT
        ) {
          if (typeof value !== 'string') {
            return this.createError({
              message: `Default value must be a string for ${type} type, but received ${typeof value}`,
            });
          }
          return true;
        }

        if (type === ControlModeTemplateFieldsType.NUMBER) {
          if (
            typeof value !== 'number' &&
            !(typeof value === 'string' && !isNaN(Number(value)))
          ) {
            return this.createError({
              message: `Default value must be a number or a numeric string for ${type} type, but received ${typeof value}`,
            });
          }
          return true;
        }

        if (type === ControlModeTemplateFieldsType.BOOLEAN) {
          if (
            typeof value !== 'boolean' &&
            value !== 'true' &&
            value !== 'false'
          ) {
            return this.createError({
              message: `Default value must be a boolean or 'true'/'false' string for ${type} type, but received ${typeof value === 'string' ? `string "${value}"` : typeof value}`,
            });
          }
          return true;
        }

        return true;
      },
    )
    .test(
      'valid-select-value',
      'Default value must be one of the list options',
      function (value) {
        if (value === undefined || value === null) {
          return true;
        }

        const { type, listOptions } = this.parent;

        if (
          type === ControlModeTemplateFieldsType.SELECT &&
          listOptions &&
          listOptions.length > 0
        ) {
          if (!listOptions.includes(String(value))) {
            return this.createError({
              message: `Default value "${value}" is not in the list of available options: [${listOptions.join(', ')}]`,
            });
          }
          return true;
        }

        return true;
      },
    )
    .test(
      'valid-number-range',
      'Default value must be within the specified range',
      function (value) {
        if (value === undefined || value === null) {
          return true;
        }

        const { type, range } = this.parent;

        if (type === ControlModeTemplateFieldsType.NUMBER && range) {
          const numValue = Number(value);
          const { min, max } = range;

          if (min !== undefined && min !== null && numValue < min) {
            return this.createError({
              message: `Default value must be greater than or equal to ${min}`,
            });
          }

          if (max !== undefined && max !== null && numValue > max) {
            return this.createError({
              message: `Default value must be less than or equal to ${max}`,
            });
          }
        }

        return true;
      },
    ),
  quickConfig: yup.boolean().required('Quick config flag is required'),
});

// Schema for settings
const settingsSchema = yup.object().shape({
  uiLabel: yup.string().required('UI Label is required'),
  controllerField: yup.string().required('Controller Field is required'),
  fields: yup
    .array()
    .of(fieldSchema)
    .required('Fields array is required')
    .min(1, 'At least one field is required')
    .test(
      'unique-controller-fields',
      'Controller fields must be unique within a setting',
      function (fields) {
        if (!fields) {
          return true;
        }

        const controllerFields = fields.map((field) => field.controllerField);
        const uniqueFields = new Set(controllerFields);

        if (uniqueFields.size !== controllerFields.length) {
          return this.createError({
            message: 'Controller fields must be unique within a setting',
          });
        }

        return true;
      },
    ),
});

// Schema for the entire template
export const templateSchema = yup.object().shape({
  name: yup.string().required('Template name is required'),
  settings: yup
    .array()
    .of(settingsSchema)
    .required('Settings array is required')
    .min(1, 'At least one setting is required')
    .test(
      'unique-controller-fields',
      'Controller fields must be unique across all settings',
      function (settings) {
        if (!settings) {
          return true;
        }

        const controllerFields = settings.map(
          (setting) => setting.controllerField,
        );
        const uniqueFields = new Set(controllerFields);

        if (uniqueFields.size !== controllerFields.length) {
          return this.createError({
            message: 'Controller fields must be unique across all settings',
          });
        }

        return true;
      },
    ),
});

// Schema for an array of templates (for import validation)
export const templatesArraySchema = yup
  .array()
  .of(templateSchema)
  .required('Templates array is required')
  .min(1, 'At least one template is required');
